import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaPlus,
  FaRobot,
  FaEdit,
  FaEye,
  FaChartPie,
  FaTimes,
  FaSpinner,
  FaBolt,
} from "react-icons/fa";
import { LuToggleLeft, LuToggleRight } from "react-icons/lu";
import {
  getUserAutomations,
  updateAutomation,
  analyzeAutomationComments,
} from "../../services/automationService";
import LoadingSpinner from "../../../components/LoadingSpinner";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import CommentAnalysisModal from "./CommentAnalysisModal";
import { INFLUENCER_BASE } from "../../constants";

const AUTOMATION_TYPE_LABEL = {
  DM: "Direct Message",
  Comments: "Comments",
  Both: "DM & Comments",
};

const AutomationDashboard = () => {
  const navigate = useNavigate();
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deactivateDialog, setDeactivateDialog] = useState({ isOpen: false, automationId: null, automationTitle: "" });
  const [deactivatingId, setDeactivatingId] = useState(null);

  // AI comment analysis state. Analysis is ONLY triggered by an explicit
  // user click (never on mount) to keep Gemini costs down.
  const [analyzingId, setAnalyzingId] = useState(null);
  const [analysisModal, setAnalysisModal] = useState(null); // { automation, result }
  const [analysisResults, setAnalysisResults] = useState({}); // { [automationId]: result }

  useEffect(() => {
    fetchAutomations();
  }, []);

  const fetchAutomations = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await getUserAutomations();

      if (response.success) {
        setAutomations(response.data || []);
      } else {
        setError(response.error || "Failed to fetch automations");
      }
    } catch (error) {
      console.error("Error fetching automations:", error);
      setError(error.error || error.message || "Failed to fetch automations");
      toast.error("Failed to load automations");
    } finally {
      setLoading(false);
    }
  };

  const handleView = (automationId) => {
    navigate(`${INFLUENCER_BASE}/dashboard/automation-dashboard/${automationId}`);
  };

  const handleEdit = (automationId) => {
    navigate(`${INFLUENCER_BASE}/dashboard/instagram-automation/${automationId}`);
  };

  const handleDeactivateClick = (automation) => {
    if (!automation?.isActive) return;
    setDeactivateDialog({
      isOpen: true,
      automationId: automation._id,
      automationTitle: automation.uploadPost?.title || "Untitled",
    });
  };

  const handleDeactivateConfirm = async () => {
    if (!deactivateDialog.automationId) return;

    try {
      setDeactivatingId(deactivateDialog.automationId);
      const response = await updateAutomation(deactivateDialog.automationId, { isActive: false });

      if (response.success) {
        toast.success("Automation has been permanently deactivated");
        setDeactivateDialog({ isOpen: false, automationId: null, automationTitle: "" });
        await fetchAutomations();
      } else {
        toast.error(response.error || "Failed to deactivate automation");
      }
    } catch (error) {
      console.error("Error deactivating automation:", error);
      toast.error(error.error || error.message || "Failed to deactivate automation");
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleDeactivateCancel = () => {
    setDeactivateDialog({ isOpen: false, automationId: null, automationTitle: "" });
  };

  const handleAnalyze = useCallback(
    async (automation) => {
      try {
        setAnalyzingId(automation._id);
        const result = await analyzeAutomationComments(automation._id);

        if (result?.success) {
          setAnalysisResults((prev) => ({ ...prev, [automation._id]: result }));
          setAnalysisModal({ automation, result });
          toast.success(
            result.cached ? "Loaded cached analysis" : "Fresh analysis ready"
          );
        } else {
          toast.error(result?.message || "Failed to analyze comments");
        }
      } catch (err) {
        const code = err?.code || err?.error;
        if (code === "NOT_ENOUGH_COMMENTS") {
          toast.info(err.message || "Not enough comments to analyze yet");
        } else if (code === "GEMINI_INVALID_RESPONSE") {
          toast.error("AI is busy right now. Please try again shortly.");
        } else {
          toast.error(err.message || err.error || "Failed to analyze comments");
        }
      } finally {
        setAnalyzingId(null);
      }
    },
    []
  );

  const getAutomationTypeDisplay = (type) => AUTOMATION_TYPE_LABEL[type] || type;

  const stats = useMemo(() => {
    const total = automations.length;
    const active = automations.filter((a) => a.isActive).length;
    return { total, active, inactive: total - active };
  }, [automations]);

  if (loading) {
    return (
      <div className="flex min-h-[240px] w-full items-center justify-center p-4 lg:p-6">
        <LoadingSpinner message="Loading automations..." />
      </div>
    );
  }

  return (
    <div className="w-full p-4 lg:p-6">
      <div className="mb-4 flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 md:flex-row md:flex-nowrap md:items-center md:justify-between lg:mb-6">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold text-gray-900 md:text-2xl lg:text-3xl">
            Automation Dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your Instagram automations and unlock AI-powered audience insights.
          </p>
        </div>
        <button
          onClick={() => navigate(`${INFLUENCER_BASE}/dashboard/instagram-automation`)}
          className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-orange-600 md:w-auto"
        >
          <FaPlus className="h-3.5 w-3.5" />
          <span>Create New Automation</span>
        </button>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3 lg:mb-6">
        <StatCard
          label="Total Automations"
          value={stats.total}
          Icon={FaRobot}
          tone="default"
        />
        <StatCard
          label="Active"
          value={stats.active}
          Icon={FaBolt}
          tone="success"
        />
        <StatCard
          label="Inactive"
          value={stats.inactive}
          Icon={FaTimes}
          tone="muted"
        />
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 lg:mb-6">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {automations.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-50">
              <FaRobot className="text-3xl text-orange-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800">
              No Automations Yet
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Create your first automation to get started
            </p>
            <button
              onClick={() => navigate(`${INFLUENCER_BASE}/dashboard/instagram-automation`)}
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-2.5 text-sm font-medium text-white transition hover:bg-orange-600"
            >
              <FaPlus />
              <span>Create Automation</span>
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
                <tr>
                  {[
                    "S.N",
                    "Post Title",
                    "Type",
                    "Keywords",
                    "Status",
                    "AI Comment Analysis",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className="whitespace-nowrap px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 bg-white">
                {automations.map((automation, index) => {
                  const result = analysisResults[automation._id];
                  return (
                    <tr
                      key={automation._id}
                      className="transition-colors hover:bg-orange-50/30"
                    >
                      <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-gray-500">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 text-sm font-semibold text-gray-900">
                        {automation.uploadPost?.title || "Untitled"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-sm">
                        <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-100">
                          {getAutomationTypeDisplay(automation.typeOfAutomation?.type)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <div className="flex max-w-[180px] flex-wrap gap-1">
                          {automation.keywords?.length > 0 ? (
                            automation.keywords.slice(0, 4).map((keyword, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
                              >
                                {keyword}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs italic text-gray-400">No keywords</span>
                          )}
                          {automation.keywords?.length > 4 && (
                            <span className="rounded-md bg-gray-50 px-2 py-0.5 text-xs text-gray-500">
                              +{automation.keywords.length - 4}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-2 py-3 text-sm sm:px-4 sm:py-4">
                        <div className="flex flex-col items-start gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                          <button
                            type="button"
                            onClick={() => handleDeactivateClick(automation)}
                            disabled={!automation.isActive || deactivatingId === automation._id}
                            className={`inline-flex items-center justify-center rounded-md p-0.5 transition-opacity ${
                              automation.isActive
                                ? "text-green-500 hover:text-green-600"
                                : "cursor-not-allowed text-gray-300"
                            } ${deactivatingId === automation._id ? "opacity-70" : ""}`}
                            title={
                              automation.isActive
                                ? "Deactivate automation"
                                : "Automation already deactivated"
                            }
                            aria-label={
                              automation.isActive
                                ? "Deactivate automation"
                                : "Automation already deactivated"
                            }
                          >
                            {automation.isActive ? (
                              <LuToggleRight className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7" />
                            ) : (
                              <LuToggleLeft className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7" />
                            )}
                          </button>
                          <span
                            className={`text-[10px] font-semibold leading-none sm:text-xs ${
                              automation.isActive ? "text-green-600" : "text-gray-400"
                            }`}
                          >
                            {automation.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-4 py-4 text-sm">
                        <AnalysisCell
                          analyzing={analyzingId === automation._id}
                          result={result}
                          onAnalyze={() => handleAnalyze(automation)}
                          onView={() =>
                            result && setAnalysisModal({ automation, result })
                          }
                        />
                      </td>

                      <td className="whitespace-nowrap px-4 py-4 text-sm">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleView(automation._id)}
                            className="rounded-lg p-2 text-orange-600 transition-colors hover:bg-orange-50"
                            title="View automation details"
                          >
                            <FaEye />
                          </button>
                          <button
                            onClick={() => handleEdit(automation._id)}
                            disabled={!automation.isActive}
                            className={`rounded-lg p-2 transition-colors ${
                              automation.isActive
                                ? "text-blue-600 hover:bg-blue-50"
                                : "cursor-not-allowed text-gray-300"
                            }`}
                            title={automation.isActive ? "Edit automation" : "Editing disabled for inactive automation"}
                          >
                            <FaEdit />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {analysisModal && (
        <CommentAnalysisModal
          automation={analysisModal.automation}
          result={analysisModal.result}
          onClose={() => setAnalysisModal(null)}
        />
      )}

      <ConfirmationDialog
        isOpen={deactivateDialog.isOpen}
        onClose={handleDeactivateCancel}
        onConfirm={handleDeactivateConfirm}
        title="Deactivate Automation"
        message={`Deactivating "${deactivateDialog.automationTitle}" will permanently turn it off. You won't be able to re-activate it later. Continue?`}
        confirmText="Deactivate"
        cancelText="Cancel"
        type="warning"
      />
    </div>
  );
};

/* ------------------------------ Sub-components ----------------------------- */

const StatCard = ({ label, value, Icon, tone = "default" }) => {
  const toneStyles = {
    default: "border-gray-200 bg-white text-gray-600",
    success: "border-emerald-100 bg-emerald-50 text-emerald-600",
    muted: "border-gray-200 bg-gray-50 text-gray-400",
    warning: "border-amber-100 bg-amber-50 text-amber-600",
    premium: "border-orange-100 bg-gradient-to-br from-orange-50 to-amber-50 text-orange-600",
  };
  return (
    <div className={`min-w-0 rounded-xl border p-3 shadow-sm sm:p-4 ${toneStyles[tone]}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm text-gray-500">{label}</p>
          <p className="mt-0.5 truncate text-lg font-bold text-gray-900 lg:text-xl">{value}</p>
        </div>
        <Icon className="h-4 w-4 shrink-0 lg:text-xl lg:h-5 lg:w-5" />
      </div>
    </div>
  );
};

/**
 * AnalysisCell
 * ------------------------------------------------------------------
 * Renders the per-row AI analysis control. Once an analysis has been
 * fetched, it shows a compact sentiment summary that opens the full
 * modal on click.
 */
const AnalysisCell = ({ analyzing, result, onAnalyze, onView }) => {
  if (analyzing) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-lg bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-500">
        <FaSpinner className="h-3.5 w-3.5 animate-spin" />
        Analyzing…
      </span>
    );
  }

  if (result?.analysis) {
    const a = result.analysis;
    const positive = a.positiveCount || 0;
    const negative = a.negativeCount || 0;
    const neutral = a.neutralCount || 0;
    const total = positive + negative + neutral || 1;
    const score = a.sentimentScore ?? 0;
    return (
      <button
        type="button"
        onClick={onView}
        className="group flex w-[150px] flex-col gap-1 rounded-lg border border-gray-100 bg-gray-50/60 px-2.5 py-1.5 text-left transition hover:border-orange-200 hover:bg-orange-50/50"
        title="View full analysis"
      >
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold text-gray-700">
            Score {score}
          </span>
          <span className="text-[10px] font-semibold text-orange-600 opacity-0 transition group-hover:opacity-100">
            View
          </span>
        </div>
        <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
          <div className="bg-emerald-500" style={{ width: `${(positive / total) * 100}%` }} />
          <div className="bg-gray-300" style={{ width: `${(neutral / total) * 100}%` }} />
          <div className="bg-rose-500" style={{ width: `${(negative / total) * 100}%` }} />
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onAnalyze}
      className="inline-flex items-center gap-1 rounded-lg tracking-tight border border-orange-200 bg-orange-50 px-1.5 md:text-xs text-[8px] font-medium text-orange-600 transition hover:bg-orange-100 md:px-3 md:py-1.5 md:gap-1.5 md:font-semibold"
      title="Analyze comments with AI"
    >
      <FaChartPie className="h-2.5 w-2.5 md:h-3.5 md:w-3.5" />
      Analyze
    </button>
  );
};

export default AutomationDashboard;
